import { useState } from "react";
import { Link, useRouteContext } from "@tanstack/react-router";
import {
	Clock,
	History,
	UsersRound,
	Banknote,
	CalendarDays,
	MessageSquareText,
	Stethoscope,
	TriangleAlert,
	UserRound,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import type { ActionCard as ActionCardData } from "@/types/actions";
import { ActionQueueLabels } from "@/lib/action-queue";
import { djs } from "@/lib/djs";
import { format } from "@/lib/format";
import { cn } from "@/lib/utils";

import { ActionCardActions } from "./action-card-actions";
import { ActionChatBar } from "./action-chat-bar";
import { ActionHistoryModal } from "./action-history-modal";
import { EvidenceModal } from "./evidence-modal";

function initialsOf(name: string) {
	const parts = name.trim().split(/\s+/u).filter(Boolean);

	if (parts.length === 0) {
		return "?";
	}

	if (parts.length === 1) {
		return parts[0].slice(0, 2).toUpperCase();
	}

	return `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
}

const destinations = {
	conversation: {
		to: "/conversas",
		search: undefined,
		icon: MessageSquareText,
		label: "Abrir conversa",
	},
	agenda: {
		to: "/agenda",
		search: undefined,
		icon: CalendarDays,
		label: "Abrir agenda",
	},
	finance: {
		to: "/financeiro",
		search: undefined,
		icon: Banknote,
		label: "Abrir financeiro",
	},
	clinical: {
		to: "/pessoa/$id",
		search: { tab: "cadastro", focus: "clinico" },
		icon: Stethoscope,
		label: "Abrir prontuário",
	},
	person: {
		to: "/pessoa/$id",
		search: { tab: "cadastro" },
		icon: UserRound,
		label: "Abrir cadastro",
	},
	people: {
		to: "/pessoas",
		search: undefined,
		icon: UsersRound,
		label: "localizar pessoa",
	},
} as const;

function ActionDestinationLink({ action }: { action: ActionCardData }) {
	const { user } = useRouteContext({ from: "/_app" });
	const surface =
		action.surface === "clinical" && user.role !== "medical" ? "person" : action.surface;
	const destination = destinations[surface];

	return (
		<Button asChild variant="outline" size="sm">
			<Link
				to={destination.to}
				params={{ id: action.person_id }}
				search={
					action.surface === "conversation" ? { person: action.person_id } : destination.search
				}
				data-slot="action-destination"
				data-destination={surface}
			>
				<destination.icon />
				{destination.label}
				{action.surface === "conversation" && action.window_open && (
					<span className="font-mono text-xs text-success">{action.window_remaining_hours}h</span>
				)}
			</Link>
		</Button>
	);
}

export function ActionCard({ action, showRole }: { action: ActionCardData; showRole: boolean }) {
	return (
		<article
			data-component="action-card"
			data-action-id={action.id}
			data-role={action.role}
			className="action-item @container"
		>
			<div
				className={cn(
					"absolute inset-y-5 left-0 w-1 rounded-r-sm",
					action.priority === "alta" ? "bg-warning" : "bg-primary/20",
				)}
			/>
			<ActionCardContent action={action} showRole={showRole} />
		</article>
	);
}

export function ActionCardContent({
	action,
	showRole,
}: {
	action: ActionCardData;
	showRole: boolean;
}) {
	const [evidenceOpen, setEvidenceOpen] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);
	const waitingFor =
		action.waiting_for === null ? "retorno" : ActionQueueLabels.waitingFor[action.waiting_for];
	const phone = format.phone(action.phone);
	const sourceCount = action.sources.length;

	return (
		<>
			<div className="action-layout">
				<div className="flex min-w-0 items-start gap-3">
					<span className="grid size-9 shrink-0 place-items-center rounded-md bg-primary-soft text-xs font-semibold text-primary">
						{initialsOf(action.name)}
					</span>
					<div className="min-w-0">
						<Link
							to="/pessoa/$id"
							params={{ id: action.person_id }}
							search={{ tab: "cadastro" }}
							className="block text-sm font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							{action.surface === "people" && "origem: "}
							{action.name}
						</Link>
						{!!phone && (
							<p className="mt-1 break-all font-mono text-xs text-muted-foreground">{phone}</p>
						)}
						<div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
							{showRole && action.role === "medica" && <span className="text-primary">Médica</span>}
							{showRole && action.role === "secretaria" && (
								<span className="text-muted-foreground">Secretária</span>
							)}
							{action.priority === "alta" && (
								<span className="status-badge bg-warning-soft text-warning">Prioridade</span>
							)}
						</div>
						{action.shared_phone && (
							<span
								data-slot="shared-phone-warning"
								className="mt-2 inline-flex items-center gap-1 text-xs text-destructive"
							>
								<TriangleAlert className="size-3.5 shrink-0" />
								telefone compartilhado
							</span>
						)}
					</div>
				</div>
				<div className="min-w-0">
					<h3 className="action-task">{action.task}</h3>
					<p className="mt-1 text-sm leading-relaxed text-muted-foreground">{action.context}</p>

					{action.status === "aguardando" && (
						<div data-slot="action-waiting" className="mt-3 rounded-md bg-secondary p-3 text-sm">
							<p className="flex items-center gap-1.5 font-medium text-foreground">
								<Clock className="size-3.5" /> aguardando {waitingFor}
							</p>
							{!!action.waiting_reason && (
								<p className="mt-1 text-muted-foreground">{action.waiting_reason}</p>
							)}
							{action.next_review_at !== null && (
								<p className="mt-1 text-xs text-muted-foreground">
									retorno em{" "}
									{djs(action.next_review_at).tz("America/Sao_Paulo").format("DD/MM HH:mm")}
								</p>
							)}
						</div>
					)}
					{action.due_at !== null && (
						<p data-slot="action-due" className="mt-3 text-xs text-muted-foreground">
							prazo em {djs(action.due_at).tz("America/Sao_Paulo").format("DD/MM HH:mm")}
						</p>
					)}

					{action.surface === "people" && (
						<p className="mt-2 text-sm">
							localize a pessoa mencionada para executar esta pendência.
						</p>
					)}
					{!!action.draft &&
						action.surface === "conversation" &&
						action.status === "aberta" &&
						action.can_manage && (
							<div className="mt-4">
								<ActionChatBar key={action.version} action={action} />
							</div>
						)}
					<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/70 pt-3">
						<ActionDestinationLink action={action} />
						<span className="flex-1" />
						<ActionCardActions action={action} />
						{action.can_manage && (
							<Button type="button" variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
								<History /> histórico
							</Button>
						)}
						{sourceCount > 0 && (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								data-slot="evidence-open"
								onClick={() => setEvidenceOpen(true)}
							>
								Fontes<span className="font-mono text-xs">{sourceCount}</span>
							</Button>
						)}
					</div>
				</div>
			</div>
			{evidenceOpen && (
				<EvidenceModal
					personId={action.person_id}
					sources={action.sources}
					title={action.task}
					onClose={() => setEvidenceOpen(false)}
				/>
			)}
			{historyOpen && <ActionHistoryModal action={action} onClose={() => setHistoryOpen(false)} />}
		</>
	);
}
